import React, { useEffect, useRef, useState } from 'react';
import { MailTemplate } from "../typings";
import { Row } from "../components/Flex";
import { useParams } from "react-router-dom";
import { PrimaryButton } from "../components/buttons/Buttons";
import { useApi } from "../api-context";
import EditableInput from "../components/EditableInput";
import { commitMailTemplate, fetchMailTemplate, testMailTemplate, updateMailTemplate } from "./email-templates-api";
import { EditorRef, EmailEditor, EmailEditorProps } from "react-email-editor";
import { toast } from "react-toastify";
import TextField from "../components/TextField";
import CreateByNameDialog from "../components/CreateByNameDialog";
import ConfirmationDialog from "../tags/ConfirmationDialog";

function EmailTemplatePage() {
    const api = useApi();
    const [template, setTemplate] = useState<MailTemplate>();

    const { id } = useParams();

    const emailEditorRef = useRef<EditorRef>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [confirmationOpen, setConfirmationOpen] = useState(false);

    const onReady: EmailEditorProps['onReady'] = (unlayer) => {
        // editor is ready
        // you can load your template here;
        // the design json can be obtained by calling
        // unlayer.loadDesign(callback) or unlayer.exportHtml(callback)
        if (template?.draft) {
            unlayer.loadDesign(JSON.parse(template.draft));
        }

        // const templateJson = { DESIGN JSON GOES HERE };
        // unlayer.loadDesign(templateJson);
    };

    const loadTemplate = () => {
        if (!id) {
            return;
        }
        fetchMailTemplate(api, Number(id))
            .then(setTemplate)
            .catch(console.error);
    };

    const onUpdateTemplate = () => {
        if (!template) {
            return;
        }
        const unlayer = emailEditorRef.current?.editor;

        unlayer?.exportHtml((data: any) => {
            const { design, html } = data;
            const draft = JSON.stringify(design);
            setTemplate({ ...template!, draft });
            updateMailTemplate(api, Number(id), { name: template.name, subject: template.subject, draft, draftHtml: html })
                .then(() => toast.success('Saved changes'))
                .catch((status) => toast.error('Failed to save changes'));
        });
    };

    const onCommitTemplate = () => {
        if (!template) {
            setConfirmationOpen(false)
            return;
        }
        commitMailTemplate(api, Number(id))
            .then(() => toast.success('Commited changes'))
            .catch(() => toast.error('Failed to commit changes'))
            .finally(() => setConfirmationOpen(false));
    };

    const onTestTemplate = (email: string) => {
        if (!template) {
            setDialogOpen(false)
            return;
        }
        testMailTemplate(api, Number(id), email)
            .then(() => toast.success('Test message sent'))
            .catch(() => toast.error('Failed to send test message'))
            .finally(() => setDialogOpen(false));
    };

    useEffect(() => {
        loadTemplate();
    }, []);

    return (
        <>
            <div className="group-page-header">
                <Row gap={8}>
                    <div style={{ flex: 1 }}>
                        <EditableInput
                            value={template?.name || ''}
                            setValue={name => {
                                setTemplate({ ...template!, name });
                            }}
                        />
                    </div>
                    <PrimaryButton
                        label="SAVE"
                        style={{ flex: 0, minWidth: '150px' }}
                        onClick={onUpdateTemplate}
                    />
                    <PrimaryButton
                        label="COMMIT"
                        style={{ flex: 0, minWidth: '150px' }}
                        onClick={() => setConfirmationOpen(true)}
                    />
                    <PrimaryButton
                        label="TEST"
                        style={{ flex: 0, minWidth: '150px' }}
                        onClick={() => setDialogOpen(true)}
                    />
                </Row>
            </div>
            <div className="page-container">
                <div className="content-container" style={{ marginBottom: '32px' }}>
                    <div className="box basic-info" style={{ gap: '12px', flex: 3 }}>
                        <TextField
                            label="Subject"
                            placeholder="Enter value"
                            value={template?.subject}
                            setValue={subject => setTemplate({ ...template!, subject })}
                        />
                        <div style={{marginTop: '20px'}}>
                            {template?.placeholders?.map(ph => (
                                <span style={{
                                    backgroundColor: 'lightgrey',
                                    borderRadius: '50px',
                                    padding: '5px 10px',
                                    marginRight: '8px'
                                }}>{ph}</span>
                            ))}
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '16px', width: '100%' }}>
                        {template && <EmailEditor ref={emailEditorRef} onReady={onReady}/>}
                    </div>
                </div>
                <CreateByNameDialog
                    label="Email"
                    title="Send test email"
                    actionLabel="Send"
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    onCreate={onTestTemplate}
                />
                <ConfirmationDialog
                    title="Commit changes"
                    description="Are you sure you want to commit draft changes to template that will be sent?"
                    open={confirmationOpen}
                    onClose={() => setConfirmationOpen(false)}
                    onConfirm={onCommitTemplate}
                />
            </div>
        </>
    );
}

export default EmailTemplatePage;
